import { authStorage } from '@/lib/auth';
import { markUserBanned } from '@/lib/bannedSession';
import type {
  AuctionCard,
  AuctionDetail,
  AuctionFilters,
  AutoBid,
  Bid,
  ItemCondition,
  MeProfile,
  Notification,
  Paginated,
  Review,
  Tag,
  UploadedPhoto,
  UserPublicProfile,
} from '@/types/domain';

export interface CreateAuctionInput {
  title: string;
  description: string;
  condition: ItemCondition;
  city: string;
  photos: UploadedPhoto[];
  start_price: number;
  min_step: number;
  buy_now_price: number | null;
  duration_minutes: number;
  tags: string[];
}

const API_BASE = import.meta.env.VITE_API_BASE_URL || '/api';

export class ApiError extends Error {
  constructor(
    public status: number,
    message: string,
    public code?: string,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

function parseErrorBody(body: Record<string, unknown>): { message: string; code?: string } {
  const raw = body.message;
  if (typeof raw === 'object' && raw !== null) {
    const obj = raw as { code?: string; message?: string };
    return {
      message: obj.message ?? 'Request failed',
      code: obj.code,
    };
  }
  if (typeof raw === 'string') {
    return {
      message: raw,
      code: typeof body.code === 'string' ? body.code : undefined,
    };
  }
  return {
    message: typeof body.error === 'string' ? body.error : 'Request failed',
    code: typeof body.code === 'string' ? body.code : undefined,
  };
}

function throwApiError(status: number, body: Record<string, unknown>): never {
  const { message, code } = parseErrorBody(body);
  if (code === 'USER_BANNED' || message === 'Your account is banned') {
    markUserBanned();
  }
  throw new ApiError(status, message, code);
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const token = authStorage.getToken();

  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...(init?.headers as Record<string, string>),
  };
  if (token) {
    headers.Authorization = `Bearer ${token}`;
  }

  const res = await fetch(`${API_BASE}${path}`, {
    ...init,
    headers,
  });

  if (!res.ok) {
    try {
      const body = await res.json();
      throwApiError(res.status, body);
    } catch (err) {
      if (err instanceof ApiError) throw err;
      throw new ApiError(res.status, `HTTP ${res.status}`);
    }
  }

  if (res.status === 204) return undefined as T;
  return res.json();
}

// ── Auth ─────────────────────────────────────────────
export interface TelegramLoginResponse {
  token: string;
  user: import('@/lib/auth').AuthUser;
}

export const authApi = {
  loginWithTelegram(initData: string): Promise<TelegramLoginResponse> {
    return request('/auth/telegram', {
      method: 'POST',
      body: JSON.stringify({ initData }),
    });
  },
};

// ── Domain API ─────────────────────────────────────────
export const api = {
  // ── Uploads ─────────────────
  // Multipart: НЕ ставим Content-Type руками — браузер сам выставит
  // multipart/form-data с boundary. Поэтому идём мимо request().
  async uploadPhoto(file: File): Promise<UploadedPhoto> {
    const token = authStorage.getToken();
    const form = new FormData();
    form.append('file', file);

    const res = await fetch(`${API_BASE}/uploads`, {
      method: 'POST',
      headers: token ? { Authorization: `Bearer ${token}` } : {},
      body: form,
    });

    if (!res.ok) {
      try {
        const body = await res.json();
        throwApiError(res.status, body);
      } catch (err) {
        if (err instanceof ApiError) throw err;
        throw new ApiError(res.status, `HTTP ${res.status}`);
      }
    }
    return res.json();
  },

  // ── Auctions ────────────────
  listAuctions(filters: AuctionFilters = {}): Promise<Paginated<AuctionCard>> {
    const params = new URLSearchParams();
    Object.entries(filters).forEach(([k, v]) => {
      if (v === undefined || v === null) return;
      if (Array.isArray(v)) v.forEach((val) => params.append(k, String(val)));
      else params.set(k, String(v));
    });
    return request(`/auctions?${params.toString()}`);
  },

  getAuction(id: string): Promise<AuctionDetail> {
    return request(`/auctions/${id}`);
  },

  listBids(auctionId: string): Promise<Bid[]> {
    return request(`/auctions/${auctionId}/bids`);
  },

  placeBid(auctionId: string, amount: number): Promise<{ bid: Bid; auction: AuctionDetail }> {
    return request(`/auctions/${auctionId}/bids`, {
      method: 'POST',
      body: JSON.stringify({ amount }),
    });
  },

  setAutoBid(
    auctionId: string,
    maxAmount: number,
    stepAmount: number | null,
  ): Promise<{ auto_bid: AutoBid; auction: AuctionDetail }> {
    return request(`/auctions/${auctionId}/auto-bid`, {
      method: 'POST',
      body: JSON.stringify({ max_amount: maxAmount, step_amount: stepAmount }),
    });
  },

  createAuction(input: CreateAuctionInput): Promise<AuctionDetail> {
    return request('/auctions', {
      method: 'POST',
      body: JSON.stringify(input),
    });
  },

  updateAuction(id: string, input: CreateAuctionInput): Promise<AuctionDetail> {
    return request(`/auctions/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(input),
    });
  },

  // ── Tags ────────────────────
  listTags(): Promise<Tag[]> {
    return request('/tags');
  },

  // ── Me ──────────────────────
  getMe(): Promise<MeProfile> {
    return request('/me');
  },

  getUserProfile(userId: string) {
    return request<UserPublicProfile & {
      is_following: boolean;
      auctions_created: number;
      member_since: string;
    }>(`/users/${userId}`);
  },

  getMyAuctions(tab: string): Promise<AuctionCard[]> {
    return request(`/me/auctions?tab=${tab}`);
  },

  getMySubscriptions(): Promise<UserPublicProfile[]> {
    return request('/me/subscriptions');
  },

  toggleSubscription(sellerId: string): Promise<{ is_following: boolean }> {
    return request(`/users/${sellerId}/subscription`, { method: 'POST' });
  },

  // ── Notifications ───────────
  listNotifications(): Promise<Notification[]> {
    return request('/notifications');
  },

  getUnreadCount(): Promise<number> {
    return request<{ count: number }>('/notifications/unread-count').then((r) => r.count);
  },

  markAllNotificationsRead(): Promise<{ updated: number }> {
    return request('/notifications/read-all', { method: 'POST' });
  },

  markNotificationRead(id: string): Promise<void> {
    return request(`/notifications/${id}/read`, { method: 'POST' });
  },

  // ── Deal (шаг 14) ───────────
  confirmDeal(auctionId: string): Promise<AuctionDetail> {
    return request(`/auctions/${auctionId}/confirm-deal`, { method: 'POST' });
  },

  refuseDeal(
    auctionId: string,
    description: string | null,
  ): Promise<{ complaintId: string }> {
    return request(`/auctions/${auctionId}/refuse-deal`, {
      method: 'POST',
      body: JSON.stringify({ description }),
    });
  },

  // ── Reviews (шаг 11) ────────
  createReview(
    auctionId: string,
    rating: number,
    comment: string | null,
  ): Promise<{ id: string; rating: number; comment: string | null }> {
    return request(`/auctions/${auctionId}/reviews`, {
      method: 'POST',
      body: JSON.stringify({ rating, comment }),
    });
  },

  buyNow(auctionId: string): Promise<AuctionDetail> {
    return request(`/auctions/${auctionId}/buy-now`, { method: 'POST' });
  },

  listUserReviews(userId: string): Promise<Review[]> {
    return request(`/users/${userId}/reviews`);
  },

  listUserAuctions(userId: string): Promise<import('@/types/domain').AuctionCard[]> {
    return request(`/users/${userId}/auctions`);
  },
};

export type Api = typeof api;
